import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { movies } from '@/mocks/movies';

const categoryMeta: Record<string, { title: string; description: string; keywords: string }> = {
  movie: {
    title: '电影大全-高清电影在线观看，最新院线大片即点即播 | 黄瓜影院',
    description: '黄瓜影院电影大全频道，汇集最新院线大片、经典佳片、热门电影，高清画质即点即播，流畅不缓冲。动作片、喜剧片、科幻片、悬疑片等海量电影分类，满足您的观影需求。免费在线观看。',
    keywords: '电影大全,最新电影,高清电影,在线观看,院线大片,动作片,喜剧片,科幻片,黄瓜影院',
  },
  tv: {
    title: '电视剧全集-免费电视剧在线观看，最新剧集完整版不缓冲 | 黄瓜影院',
    description: '黄瓜影院电视剧频道，提供免费电视剧全集在线观看。涵盖最新剧集完整版、热播电视剧、经典国产剧，高清画质，流畅不缓冲。古装剧、都市剧、悬疑剧等海量片库，快速找片，智能推荐。',
    keywords: '免费电视剧,电视剧全集,最新剧集,完整版,高清在线观看,热播电视剧,古装剧,都市剧,黄瓜影院',
  },
  anime: {
    title: '动漫大全-高清动漫在线观看，热门国漫日漫即点即播 | 黄瓜影院',
    description: '黄瓜影院动漫频道，提供高清动漫在线观看。涵盖最新国漫、热门日漫、经典动画电影，高清画质流畅不缓冲。热血、治愈、奇幻等海量动漫分类，满足二次元爱好者的追番需求。',
    keywords: '动漫大全,高清动漫,在线观看,国漫,日漫,动画电影,热血动漫,治愈动漫,黄瓜影院',
  },
  variety: {
    title: '综艺节目-热门综艺在线观看，搞笑娱乐音乐竞技 | 黄瓜影院',
    description: '黄瓜影院综艺频道，汇集热门综艺节目在线观看。搞笑娱乐、音乐竞技、真人秀等精彩综艺应有尽有，高清画质流畅播放。最新一期第一时间更新，让您不错过每一个精彩瞬间。',
    keywords: '综艺节目,热门综艺,在线观看,搞笑综艺,音乐综艺,真人秀,竞技综艺,黄瓜影院',
  },
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const meta = slug ? categoryMeta[slug] : null;

  const categoryMovies = slug
    ? movies.filter((m) => m.category === slug)
    : [];

  if (!category || !meta) {
    return (
      <>
        <Navbar />
        <div className="pt-20 px-4 text-center">
          <h2 className="text-xl text-foreground-700">分类不存在</h2>
          <Link to="/" className="text-primary-500 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://huanggua.tv' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `https://huanggua.tv/category/${slug}` },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={`https://huanggua.tv/category/${slug}`}
        ogType="website"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: category.name },
              ]}
            />

            <div className="mt-6 mb-10">
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-3">
                {category.name}专区
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
                {category.description}
              </p>
            </div>

            {/* Sub-category tabs */}
            <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-1">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                {categoryMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">该分类暂无内容，请稍后再来</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}